import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../components/ui/alert-dialog";
import {
  listRentalContracts,
  createRentalContract,
  previewRentalContract,
  cancelRentalContract,
  generateRentalContractPdf,
  getRentalContractPdfUrl,
} from "../../../api/adminData";
import { formatDateOnly } from "../../../utils/datetime";

/**
 * Read-only-ish rental-contract section for one tenancy (Phase 3a.4).
 *
 * - Lists the tenancy's rental contracts.
 * - "Entwurf erstellen" creates a draft (existing Sprint-2 POST); in-flight
 *   protected so a double-click can't create multiple versions.
 * - "Vorschau" fetches the render-only preview (Bearer auth) and shows the
 *   HTML in a Dialog via <iframe srcDoc> with sandbox="" (fully isolated).
 *   Never uses iframe src to the protected endpoint.
 *
 * No final-generate / download / PDF actions in this slice.
 */

const STATUS_LABELS_DE = {
  draft: "Entwurf",
  generated: "Generiert",
  signed: "Signiert",
  superseded: "Ersetzt",
  cancelled: "Storniert",
};

function statusLabel(status) {
  return STATUS_LABELS_DE[status] || status || "—";
}

// Exported so the tenancy Dokumente tab prints a contract's status with THIS
// wording rather than a second German map that drifts from it.
export { statusLabel as rentalContractStatusLabel };

// Destructive (soft-cancel) action availability + copy per status.
// draft/generated are cancellable; signed/cancelled/superseded are not.
function destructiveLabel(status) {
  if (status === "draft") return "Entwurf verwerfen";
  if (status === "generated") return "Stornieren";
  return null;
}

function cancelReasonFor(status) {
  return status === "draft" ? "Entwurf verworfen" : "Vertrag storniert";
}

function confirmTextFor(status) {
  return status === "draft"
    ? "Diesen Entwurf wirklich verwerfen?"
    : "Diesen generierten Vertrag wirklich stornieren?";
}

function isHistoricalStatus(status) {
  return status === "cancelled" || status === "superseded";
}

// PDF generation/re-generation is only valid for non-immutable statuses
// (backend rejects signed/cancelled/superseded with 409). Used to gate both
// "PDF generieren" (no PDF yet) and "PDF neu generieren" (PDF exists).
function isImmutableStatus(status) {
  return status === "signed" || status === "cancelled" || status === "superseded";
}

export default function RentalContractSection({ tenancyId }) {
  const [contracts, setContracts] = useState(null); // null = loading
  const [listError, setListError] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [previewingId, setPreviewingId] = useState(null);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewWarnings, setPreviewWarnings] = useState([]);

  // Soft-cancel confirm target { id, status } | null, + in-flight id.
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancellingId, setCancellingId] = useState(null);

  // Per-contract PDF action in-flight ids (Phase 3a.7). pdfBusyId tracks an
  // in-flight generate/re-generate (mutates the list); openingId tracks an
  // in-flight "PDF öffnen" (no list change). Per-row so other rows stay usable.
  const [pdfBusyId, setPdfBusyId] = useState(null);
  const [openingId, setOpeningId] = useState(null);

  const loadList = useCallback(async () => {
    if (!tenancyId) return;
    setListError("");
    try {
      const items = await listRentalContracts(tenancyId);
      setContracts(Array.isArray(items) ? items : []);
    } catch (err) {
      setContracts([]);
      setListError(err?.message || "Mietverträge konnten nicht geladen werden.");
      toast.error(err?.message || "Mietverträge konnten nicht geladen werden.");
    }
  }, [tenancyId]);

  useEffect(() => {
    setContracts(null);
    loadList();
  }, [loadList]);

  const handleCreate = useCallback(async () => {
    if (isCreating) return;
    setIsCreating(true);
    try {
      await createRentalContract(tenancyId, {});
      toast.success("Vertragsentwurf erstellt.");
      await loadList();
    } catch (err) {
      toast.error(err?.message || "Vertragsentwurf konnte nicht erstellt werden.");
    } finally {
      setIsCreating(false);
    }
  }, [isCreating, tenancyId, loadList]);

  const handlePreview = useCallback(async (contractId) => {
    if (!contractId) return;
    setPreviewingId(contractId);
    try {
      const data = await previewRentalContract(contractId);
      setPreviewHtml(data?.html || "");
      setPreviewWarnings(Array.isArray(data?.warnings) ? data.warnings : []);
      setPreviewOpen(true);
    } catch (err) {
      // Do not open stale preview content on failure.
      setPreviewHtml("");
      setPreviewWarnings([]);
      setPreviewOpen(false);
      toast.error(err?.message || "Vorschau konnte nicht geladen werden.");
    } finally {
      setPreviewingId(null);
    }
  }, []);

  const handleDialogChange = useCallback((open) => {
    setPreviewOpen(open);
    if (!open) {
      // Clear so a later failed preview can't reveal stale content.
      setPreviewHtml("");
      setPreviewWarnings([]);
    }
  }, []);

  const handleGeneratePdf = useCallback(
    async (contractId, force) => {
      if (!contractId || pdfBusyId === contractId) return;
      setPdfBusyId(contractId);
      try {
        await generateRentalContractPdf(contractId, { force });
        toast.success(force ? "PDF neu generiert." : "PDF generiert.");
        await loadList();
      } catch (err) {
        toast.error(err?.message || "PDF konnte nicht generiert werden.");
      } finally {
        setPdfBusyId(null);
      }
    },
    [pdfBusyId, loadList]
  );

  const handleOpenPdf = useCallback(
    async (contractId) => {
      if (!contractId || openingId === contractId) return;
      // Open the tab SYNCHRONOUSLY (before awaiting) so the browser does not
      // treat it as a programmatic popup and block it. Do NOT pass
      // "noopener,noreferrer": noopener can make window.open return null, which
      // would null the handle and break the open-then-navigate (async) flow.
      const pdfWindow = window.open("", "_blank");
      // Sever the opener manually for hygiene (keeps the handle usable).
      if (pdfWindow) {
        try {
          pdfWindow.opener = null;
        } catch (_) {
          /* cross-origin / read-only opener — ignore */
        }
      }
      setOpeningId(contractId);
      try {
        const result = await getRentalContractPdfUrl(contractId);
        if (!result?.url) {
          pdfWindow?.close();
          toast.error("PDF-Link konnte nicht geladen werden.");
          return;
        }
        if (pdfWindow) {
          pdfWindow.location = result.url;
        } else {
          // Popup was blocked — no window to navigate.
          toast.error("Popup wurde blockiert. Bitte Popups erlauben.");
        }
      } catch (err) {
        pdfWindow?.close();
        toast.error(err?.message || "PDF-Link konnte nicht geladen werden.");
      } finally {
        setOpeningId(null);
      }
    },
    [openingId]
  );

  const handleCancelConfirm = useCallback(async () => {
    if (!cancelTarget) return;
    const { id, status } = cancelTarget;
    setCancellingId(id);
    try {
      await cancelRentalContract(id, {
        cancellation_reason: cancelReasonFor(status),
      });
      toast.success(status === "draft" ? "Entwurf verworfen." : "Vertrag storniert.");
      setCancelTarget(null);
      await loadList();
    } catch (err) {
      toast.error(err?.message || "Aktion fehlgeschlagen.");
    } finally {
      setCancellingId(null);
    }
  }, [cancelTarget, loadList]);

  const handleCancelDialogChange = useCallback((open) => {
    if (!open && cancellingId == null) {
      setCancelTarget(null);
    }
  }, [cancellingId]);

  const labelCls =
    "mb-2 text-[10px] font-bold uppercase tracking-[0.8px] text-[#64748b] dark:text-[#6b7a9a]";
  const btnCls =
    "rounded-[6px] border border-black/10 bg-transparent px-[10px] py-[3px] text-[10px] text-[#64748b] transition-colors hover:bg-slate-100 dark:border-white/[0.1] dark:text-[#8090b0] dark:hover:bg-white/[0.04] disabled:cursor-default disabled:opacity-50";

  return (
    <div className="mt-3">
      <p className={labelCls}>Mietvertrag</p>

      {contracts === null ? (
        <p className="text-[11px] text-[#64748b] dark:text-[#8090b0]">Wird geladen …</p>
      ) : contracts.length === 0 ? (
        <div className="flex flex-col items-start gap-2">
          <p className="text-[11px] text-[#64748b] dark:text-[#8090b0]">
            Noch kein Mietvertrag vorhanden.
          </p>
          <button
            type="button"
            onClick={handleCreate}
            disabled={isCreating}
            className={btnCls}
          >
            {isCreating ? "Wird erstellt …" : "Entwurf erstellen"}
          </button>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <ul className="flex flex-col gap-1">
            {contracts.map((c) => (
              <li
                key={c.id}
                data-testid={`rc-row-${c.id}`}
                className={`flex items-center justify-between gap-3 rounded-[8px] border border-black/10 px-3 py-2 dark:border-white/[0.08]${
                  isHistoricalStatus(c.status) ? " opacity-60" : ""
                }`}
              >
                <div className="min-w-0 text-[11px] text-[#0f172a] dark:text-[#eef2ff]">
                  <span className="font-mono">
                    {c.display_reference || `Version ${c.version}`}
                  </span>
                  <span className="ml-2 inline-flex rounded-full border border-black/10 bg-slate-100 px-[6px] py-[1px] text-[9px] font-semibold text-[#64748b] dark:border-white/[0.08] dark:bg-[#111520] dark:text-[#8090b0]">
                    {statusLabel(c.status)}
                  </span>
                  <span className="ml-2 text-[10px] text-[#64748b] dark:text-[#6b7a9a]">
                    v{c.version}
                  </span>
                  {c.generated_at ? (
                    <div className="mt-0.5 text-[10px] text-[#64748b] dark:text-[#6b7a9a]">
                      Generiert am {formatDateOnly(c.generated_at)}
                    </div>
                  ) : null}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <button
                    type="button"
                    onClick={() => handlePreview(c.id)}
                    disabled={previewingId === c.id}
                    className={btnCls}
                  >
                    {previewingId === c.id ? "Lädt …" : "Vorschau"}
                  </button>
                  {c.has_pdf ? (
                    <button
                      type="button"
                      onClick={() => handleOpenPdf(c.id)}
                      disabled={openingId === c.id}
                      className={btnCls}
                    >
                      {openingId === c.id ? "Öffnet …" : "PDF öffnen"}
                    </button>
                  ) : !isImmutableStatus(c.status) ? (
                    <button
                      type="button"
                      onClick={() => handleGeneratePdf(c.id, false)}
                      disabled={pdfBusyId === c.id}
                      className={btnCls}
                    >
                      {pdfBusyId === c.id ? "Generiert …" : "PDF generieren"}
                    </button>
                  ) : null}
                  {c.has_pdf && !isImmutableStatus(c.status) ? (
                    <button
                      type="button"
                      onClick={() => handleGeneratePdf(c.id, true)}
                      disabled={pdfBusyId === c.id}
                      className={btnCls}
                    >
                      {pdfBusyId === c.id ? "Generiert …" : "PDF neu generieren"}
                    </button>
                  ) : null}
                  {destructiveLabel(c.status) ? (
                    <button
                      type="button"
                      onClick={() => setCancelTarget({ id: c.id, status: c.status })}
                      disabled={cancellingId === c.id}
                      className="rounded-[6px] border border-rose-500/30 bg-transparent px-[10px] py-[3px] text-[10px] text-rose-600 transition-colors hover:bg-rose-50 dark:border-rose-400/30 dark:text-rose-400 dark:hover:bg-rose-950/30 disabled:cursor-default disabled:opacity-50"
                    >
                      {cancellingId === c.id ? "…" : destructiveLabel(c.status)}
                    </button>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
          <button
            type="button"
            onClick={handleCreate}
            disabled={isCreating}
            className={`${btnCls} self-start`}
          >
            {isCreating ? "Wird erstellt …" : "Entwurf erstellen"}
          </button>
        </div>
      )}

      {listError ? (
        <p className="mt-1 text-[10px] text-red-600 dark:text-red-400">{listError}</p>
      ) : null}

      <Dialog open={previewOpen} onOpenChange={handleDialogChange}>
        <DialogContent className="max-w-[900px]">
          <DialogHeader>
            <DialogTitle>Mietvertrag Vorschau</DialogTitle>
          </DialogHeader>

          {previewWarnings.length > 0 ? (
            <div className="rounded-[8px] border border-amber-400/50 bg-amber-50 px-3 py-2 text-[11px] text-amber-800 dark:border-amber-400/30 dark:bg-amber-950/30 dark:text-amber-300">
              <p className="mb-1 font-semibold">
                Hinweise ({previewWarnings.length}) — fehlende Angaben:
              </p>
              <ul className="list-disc pl-4">
                {previewWarnings.map((w, i) => (
                  <li key={w?.code || i}>{w?.message || w?.code || String(w)}</li>
                ))}
              </ul>
            </div>
          ) : null}

          {previewOpen && previewHtml ? (
            <iframe
              title="Mietvertrag Vorschau"
              srcDoc={previewHtml}
              sandbox=""
              className="h-[70vh] w-full rounded-[8px] border border-black/10 bg-white dark:border-white/[0.08]"
            />
          ) : null}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!cancelTarget} onOpenChange={handleCancelDialogChange}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {cancelTarget?.status === "draft"
                ? "Entwurf verwerfen"
                : "Vertrag stornieren"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {cancelTarget ? confirmTextFor(cancelTarget.status) : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={cancellingId != null}>
              Abbrechen
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancelConfirm}
              disabled={cancellingId != null}
              className="bg-rose-600 hover:bg-rose-700 focus:ring-rose-600"
            >
              {cancellingId != null
                ? "Wird ausgeführt …"
                : cancelTarget?.status === "draft"
                  ? "Verwerfen"
                  : "Stornieren"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
